import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import loadSDK from "core/utils/loadSDK";
import apm from "core/utils/apm";
import Grid from "@mui/material/Grid";
import formatCurrency from "core/utils/currency";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";

import { useTranslation } from "react-i18next";

/* global cp */
const SRC = "https://widget.cloudpayments.ru/bundles/cloudpayments.js";
const ID = "cloudpaymentswidget";

/**
 * Компонент реализует подгрузку SDK CloudPayments и механизм создания платежа с помощью открытия виджета
 *
 * @param {Object} paymentData - Данные для создания платежа
 * @param {String} returnUrl - Адрес страницы на которую необходимо редиректнуть после оплаты
 * @param {Object} amount - Сумма с валютой
 * @param {Object} descriptionComponent - Компонент описания подписки
 */
export default function CloudPaymentsProcessor({
  descriptionComponent,
  paymentData,
  returnUrl,
  amount,
  setError,
  isConnectPayment,
}) {
  const [loading, setLoading] = useState(true);
  const [isSdkReady, setIsSdkReady] = useState(false);

  const { t } = useTranslation("arena");

  const amountText = formatCurrency(
    isConnectPayment ? 1 : amount.value,
    amount.currency
  );

  useEffect(() => {
    if (!process.browser) return;
    if (!paymentData) return;

    loadSDK(SRC, ID)
      .then(() => {
        setIsSdkReady(true);
        setLoading(false);
        return true;
      })
      .catch((error) => {
        apm.captureError(error);
        if (setError) {
          setError(error);
        }
        console.log(error);
      });
  }, [paymentData, setError]);

  const onClick = () => {
    if (!paymentData) {
      return;
    }

    if (!isSdkReady || typeof cp === "undefined" || !cp.CloudPayments) {
      const error = new Error("CloudPayments SDK is not loaded");
      apm.captureError(error);
      if (setError) {
        setError(error);
      }
      return;
    }

    setLoading(true);

    const widget = new cp.CloudPayments();

    widget.oncomplete = (result) => {
      const status = result?.status;
      const type = result?.type;

      if (type === "cancel" || status === "cancel") {
        setLoading(false);
        return;
      }

      if (status === "success") {
        window.location = returnUrl;
        return;
      }

      if (setError) {
        const error = new Error(
          result?.message || "CloudPayments payment failed"
        );
        setError(error);
      }

      setLoading(false);
    };

    widget.start(paymentData).catch((error) => {
      apm.captureError(error);
      setLoading(false);
      if (setError) {
        setError(error);
      }
      console.log(error);
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Fade in={loading}>
          <LinearProgress />
        </Fade>

        {descriptionComponent && (
          <Grid item xs={12}>
            {descriptionComponent}
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            disableElevation
            fullWidth
            variant="contained"
            color="primary"
            onClick={onClick}
          >
            {t("stripePaymentForm.pay", { amount: amountText })}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

CloudPaymentsProcessor.propTypes = {
  descriptionComponent: PropTypes.object,
  paymentData: PropTypes.object,
  returnUrl: PropTypes.string,
  amount: PropTypes.object.isRequired,
  setError: PropTypes.func,
  isConnectPayment: PropTypes.bool,
};
